import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { AdminResponseDto, StoreResponseDto } from 'src/generated_client';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from '../local-storage.service';

interface RefreshResponseDto {
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  cdAdmin?: number;
  dsUsername?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly adminTokenKey = 'token';
  private readonly adminRefreshTokenKey = 'refreshToken';
  private readonly adminIdKey = 'id';
  private readonly adminNameKey = 'name';

  private readonly storeTokenKey = 'storeToken';
  private readonly storeRefreshTokenKey = 'storeRefreshToken';
  private readonly storeIdKey = 'storeId';
  private readonly storeEmailKey = 'storeName';
  private readonly storeDisplayNameKey = 'dsName';
  private readonly legacyAccessTokenKey = 'accessToken';

  private adminTokenInMemory: string | null = null;
  private storeTokenInMemory: string | null = null;
  private refreshInFlight$: Observable<string | null> | null = null;

  private readonly adminAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  readonly adminAuthenticated$ = this.adminAuthenticatedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private router: Router,
  ) {
    this.initializeSession();
  }

  private toAbsoluteApiUrl(pathOrUrl: string): string {
    if (!pathOrUrl) {
      return environment.apiBaseUrl;
    }

    if (/^https?:\/\//i.test(pathOrUrl)) {
      return pathOrUrl;
    }

    const baseUrl = (environment.apiBaseUrl || '').replace(/\/$/, '');
    const endpoint = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${baseUrl}${endpoint}`;
  }

  private normalizeToken(token: unknown): string | null {
    if (typeof token !== 'string') {
      return null;
    }

    const normalizedToken = token.trim();

    if (!normalizedToken || normalizedToken === 'undefined' || normalizedToken === 'null') {
      return null;
    }

    return normalizedToken;
  }

  initializeSession(): void {
    const adminToken = this.normalizeToken(
      this.localStorageService.getItem(this.adminTokenKey) ||
      this.localStorageService.getItem(this.legacyAccessTokenKey)
    );

    this.adminTokenInMemory = adminToken;
    this.storeTokenInMemory = this.normalizeToken(this.localStorageService.getItem(this.storeTokenKey));
    this.adminAuthenticatedSubject.next(!!adminToken);
  }

  currentUser(): AdminResponseDto {
    return {
      token: this.getToken() ?? undefined,
      refreshToken: this.getAdminRefreshToken() ?? undefined,
      cdAdmin: this.localStorageService.getItem(this.adminIdKey),
      dsUsername: this.localStorageService.getItem(this.adminNameKey),
    };
  }

  currentUserStore(): StoreResponseDto {
    return {
      token: this.getStoreToken() ?? undefined,
      cdStore: this.localStorageService.getItem(this.storeIdKey),
      dsEmail: this.localStorageService.getItem(this.storeEmailKey),
      dsName: this.localStorageService.getItem(this.storeDisplayNameKey)
    };
  }

  private persistAdminSession(
    token: string | null,
    refreshToken: string | null,
    cdAdmin?: number | string,
    dsUsername?: string,
  ): void {
    this.adminTokenInMemory = token;

    if (token) {
      this.localStorageService.setItem(this.adminTokenKey, token);
      this.localStorageService.setItem(this.legacyAccessTokenKey, token);
    } else {
      this.localStorageService.removeItem(this.adminTokenKey);
      this.localStorageService.removeItem(this.legacyAccessTokenKey);
    }

    if (refreshToken) {
      this.localStorageService.setItem(this.adminRefreshTokenKey, refreshToken);
    } else {
      this.localStorageService.removeItem(this.adminRefreshTokenKey);
    }

    if (cdAdmin !== undefined && cdAdmin !== null && cdAdmin !== '') {
      this.localStorageService.setItem(this.adminIdKey, cdAdmin);
    }

    if (dsUsername !== undefined && dsUsername !== null && dsUsername !== '') {
      this.localStorageService.setItem(this.adminNameKey, dsUsername);
    }

    this.adminAuthenticatedSubject.next(!!token);
  }

  login(x: AdminResponseDto): void {
    const token = this.normalizeToken(x?.token || (x as any)?.accessToken);
    const refreshToken = this.normalizeToken(x?.refreshToken);

    this.persistAdminSession(token, refreshToken, x?.cdAdmin, x?.dsUsername);
  }

  loginStore(x: StoreResponseDto): void {
    const token = this.normalizeToken(x?.token);
    this.storeTokenInMemory = token;

    if (token) {
      this.localStorageService.setItem(this.storeTokenKey, token);
    } else {
      this.localStorageService.removeItem(this.storeTokenKey);
    }

    this.localStorageService.setItem(this.storeIdKey, x?.cdStore || '');
    this.localStorageService.setItem(this.storeEmailKey, x?.dsEmail || '');
    this.localStorageService.setItem(this.storeDisplayNameKey, x?.dsName || '');
  }

  private clearAdminSession(): void {
    this.adminTokenInMemory = null;
    this.localStorageService.removeItem(this.adminTokenKey);
    this.localStorageService.removeItem(this.adminRefreshTokenKey);
    this.localStorageService.removeItem(this.legacyAccessTokenKey);
    this.localStorageService.removeItem(this.adminIdKey);
    this.localStorageService.removeItem(this.adminNameKey);
    this.adminAuthenticatedSubject.next(false);
  }

  private clearStoreSession(): void {
    this.storeTokenInMemory = null;
    this.localStorageService.removeItem(this.storeTokenKey);
    this.localStorageService.removeItem(this.storeRefreshTokenKey);
    this.localStorageService.removeItem(this.storeIdKey);
    this.localStorageService.removeItem(this.storeEmailKey);
    this.localStorageService.removeItem(this.storeDisplayNameKey);
  }

  clearAuthState(): void {
    this.clearAdminSession();
    this.clearStoreSession();
    this.refreshInFlight$ = null;
  }

  logout(): void {
    this.revokeAdminRefreshToken().subscribe();
    this.clearAdminSession();
    this.router.navigate(['/login-admin']);
  }

  logoutStore(): void {
    this.clearStoreSession();
    this.router.navigate(['/login-shop']);
  }

  getToken(): string | null {
    if (this.adminTokenInMemory) {
      return this.adminTokenInMemory;
    }

    const token = this.normalizeToken(
      this.localStorageService.getItem(this.adminTokenKey) ||
      this.localStorageService.getItem(this.legacyAccessTokenKey)
    );

    this.adminTokenInMemory = token;
    this.adminAuthenticatedSubject.next(!!token);
    return token;
  }

  getStoreToken(): string | null {
    if (this.storeTokenInMemory) {
      return this.storeTokenInMemory;
    }

    const token = this.normalizeToken(this.localStorageService.getItem(this.storeTokenKey));
    this.storeTokenInMemory = token;
    return token;
  }

  getAdminRefreshToken(): string | null {
    return this.normalizeToken(this.localStorageService.getItem(this.adminRefreshTokenKey));
  }

  isAdminAuthenticated(): boolean {
    return !!this.getToken();
  }

  refreshAdminToken(): Observable<string | null> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.getAdminRefreshToken();

    if (!refreshToken) {
      return of(null);
    }

    const refreshEndpoint = this.toAbsoluteApiUrl(environment.auth.refreshEndpoint);

    this.refreshInFlight$ = this.http.post<RefreshResponseDto>(refreshEndpoint, { refreshToken }).pipe(
      map((response: RefreshResponseDto) => {
        const nextToken = this.normalizeToken(response?.token || response?.accessToken);
        const nextRefreshToken = this.normalizeToken(response?.refreshToken) || refreshToken;

        if (!nextToken) {
          return null;
        }

        this.persistAdminSession(
          nextToken,
          nextRefreshToken,
          response?.cdAdmin ?? this.localStorageService.getItem(this.adminIdKey),
          response?.dsUsername ?? this.localStorageService.getItem(this.adminNameKey)
        );

        return nextToken;
      }),
      catchError(() => of(null)),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshInFlight$;
  }

  revokeAdminRefreshToken(): Observable<boolean> {
    const refreshToken = this.getAdminRefreshToken();

    if (!refreshToken) {
      return of(false);
    }

    const revokeEndpoint = this.toAbsoluteApiUrl(environment.auth.revokeEndpoint);

    return this.http.post<any>(revokeEndpoint, { refreshToken }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}
